import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {
    CompanyTypeService, LibraryService,
    LoadingSpinnerService,
    ProjectAnalysisDefinitionService,
    ToastService
} from '../../services';
import {Category, ICategoryData, IQuestionData, LibraryEntry} from '../../models';
import {Subscription} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {Question} from '../../models/definition/question';
import {
    DeleteItemModalComponent,
    TisCreateUpdateCategoryModalComponent,
    ProjectAnalysisQuestionModalComponent
} from '../../components';
import {VwuiModalService} from '@bvgo-ict/vwui-angular';
import {TranslateService} from '@ngx-translate/core';
import {CompanyType} from '../../models/user/user';
import {take} from 'rxjs/operators';
import {ProjectAnalysisDefinition} from "../../models/project-analysis";

@Component({
    selector: 'app-page-project-analysis',
    templateUrl: './page-project-analysis.component.html',
    styleUrls: ['./page-project-analysis.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PageProjectAnalysisComponent implements OnInit, OnDestroy {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private definitionService = inject(ProjectAnalysisDefinitionService);
    private modalService = inject(VwuiModalService);
    private toastService = inject(ToastService);
    private companyTypeService = inject(CompanyTypeService);
    private translateService = inject(TranslateService);
    public loadingSpinnerService = inject(LoadingSpinnerService);
    private libraryService = inject(LibraryService);

    public isUpdatingDefinition = false;
    public categories: Category[];
    private projectTypeId: number;
    public companyType: CompanyType;
    private definitionId: number;
    public definition: ProjectAnalysisDefinition;
    private subscriptions: Subscription[] = [];
    private libraries: LibraryEntry[];

    ngOnInit(): void {
        this.projectTypeId = parseInt(this.route.snapshot.paramMap.get('projectTypeId'), 10);
        this.subscriptions.push(this.companyTypeService.getActiveCompanyTypeSubject().subscribe((companyType) => {
            if (companyType) {
                this.companyType = companyType;
                this.getDefinition();
                this.getLibraryEntries();
            }
        }));
    }

    ngOnDestroy() {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriptions = [];
    }

    public updateCategoryTables(): void {
        this.categories = this.definition.categories;
    }

    public navigateToList(): void {
        this.router.navigate(['/beheer', 'project-types', this.projectTypeId]);
    }

    public isConcept(): boolean {
        return this.definition?.isConcept;
    }

    /**
     * Submit all changes to the backend.
     */
    public updateDefinition(): void {
        if (!this.isConcept()) {
            return;
        }

        const update = this.definition.cloneData();
        // remove the observer from the payload
        delete update.onChange;
        this.flattenCategories(update);
        this.isUpdatingDefinition = true;
        this.definitionService.updateDefinition(this.projectTypeId, update)
            .pipe(finalize(() => this.isUpdatingDefinition = false))
            .subscribe(() => {
                this.toastService.openSuccess(this.translateService.instant('PROJECT_ANALYSIS_VERSION.updated'));
            });
    }

    public openCreateCategoryModal(): void {
        const modal = this.modalService.open(
            // XXX Observe we re-use the tis modal
            TisCreateUpdateCategoryModalComponent,
            {
                closeOnBackdropClick: false,
                data: {
                    title: 'COMPONENT.tis.modal.create_category.title',
                    data: {
                        name: ''
                    },
                    saveText: 'BUTTON.create'
                }
            }
        );

        modal.afterClosed
            .pipe(take(1))
            .subscribe((data: ICategoryData) => {
                if (data) {
                   this.definition.addCategory(data);
                }
            });
    }

    public openUpdateCategoryModal(category: Category): void {
        const modal = this.modalService.open(
            // XXX Observe we re-use the tis modal
            TisCreateUpdateCategoryModalComponent,
            {
                closeOnBackdropClick: false,
                data: {
                    title: 'COMPONENT.tis.modal.update_category.title',
                    data: category.cloneData(),
                    saveText: 'BUTTON.update'
                }
            }
        );

        modal.afterClosed
            .pipe(take(1))
            .subscribe((data: ICategoryData) => {
                if (data) {
                    category.setData(data);
                }
            });
    }

    public openCreateQuestionModal(category: Category): void {
        const modal = this.modalService.open(
            ProjectAnalysisQuestionModalComponent,
            {
                closeOnBackdropClick: false,
                closeOnEsc: false,
                data: {
                    title: 'COMPONENT.tis.modal.create_question.title',
                    data: {
                        tempId: crypto.randomUUID(),
                        name: '',
                        possibleAnswers: []
                    },
                    libraries: this.libraries,
                    definition: this.definition,
                    saveText: 'BUTTON.create'
                },
                modalClass: 'project-analysis-question-modal'
            }
        );

        modal.afterClosed
            .pipe(take(1))
            .subscribe((data: IQuestionData) => {
                if (data) {
                    category.addSubCategoryQuestion(data);
                }
            });
    }

    public openUpdateQuestionModal(question: Question): void {
        const modal = this.modalService.open(
            ProjectAnalysisQuestionModalComponent,
            {
                closeOnBackdropClick: false,
                closeOnEsc: false,
                data: {
                    title: 'COMPONENT.tis.modal.update_question.title',
                    libraries: this.libraries,
                    data: {...question.cloneData()},
                    definition: this.definition,
                    saveText: 'BUTTON.update'
                },
                modalClass: 'project-analysis-question-modal'
            }
        );

        modal.afterClosed
            .pipe(take(1))
            .subscribe((data: IQuestionData) => {
                if (data) {
                    question.setData(data);
                }
            });
    }

    public openDeleteCategoryModal(category: Category): void {
        const modal = this.modalService.open(
            DeleteItemModalComponent,
            {
                data: {
                    title: 'COMPONENT.tis.modal.delete_category.title',
                    description: 'COMPONENT.tis.modal.delete_category.description'
                }
            }
        );

        modal.afterClosed
            .pipe(take(1))
            .subscribe((doDelete: boolean) => {
                if (doDelete) {
                    category.delete();
                }
            });
    }

    public openDeleteQuestionModal(question: Question): void {
        const modal = this.modalService.open(
            DeleteItemModalComponent,
            {
                data: {
                    title: 'COMPONENT.tis.modal.delete_question.title',
                    description: 'COMPONENT.tis.modal.delete_question.description',
                    extraDescription: question.calculatesQuestions.length ? 'COMPONENT.tis.modal.delete_question.delete_dependent_description' : null
                }
            }
        );

        modal.afterClosed.subscribe((doDelete: boolean) => {
            if (doDelete) {
                question.delete();
            }
        });
    }

    public moveCategoryUp(category: Category): void {
        category.moveUp();
    }

    public moveCategoryDown(category: Category): void {
        category.moveDown();
    }

    public moveQuestionUp(question: Question, base: Category): void {
        base.moveQuestionUp(question);
    }

    public moveQuestionDown(question: Question, base: Category): void {
        base.moveQuestionDown(question);
    }

    public trackCategory(index: number, category: Category): string {
        return `${category.id}`;
    }

    public trackQuestion(index: number, question: Question): string {
        return `${question.getParent().id}_${question.id}`;
    }

    /**
     * Not pretty, but we need to flatten all the questions, since we are using some Quickscan subcategory logic.
     *
     * <p>The problem starts when questions are pushed up or down, then the logic will create subcategories and move all
     * the questions from `category.questions` to the `subCategories[*].questions`.
     */
    private flattenCategories(definition: ProjectAnalysisDefinition): void {
        definition.categories.forEach((category) => {
            if (category.subCategories?.length > 0) {
                const questions = category.subCategories.flatMap(category => category.questions ?? []);
                if (category.questions?.length) {
                    category.questions = [...category.questions, ...questions];
                } else {
                    category.questions = questions;
                }
            }
        });
    }

    private async getLibraryEntries(): Promise<void> {
        await this.libraryService.getRiskAndOpportunitiesLibraries(this.companyType.id)
            .then((libraries) => {
                this.libraries = libraries.flatMap(library => library.getLibraryEntries());
            });
    }

    private getDefinition(): void {
        this.loadingSpinnerService.show();
        this.definitionId = parseInt(this.route.snapshot.paramMap.get('id'), 10);
        this.definitionService.getDefinition(this.projectTypeId, this.definitionId)
            .pipe(finalize(() => this.loadingSpinnerService.hide()))
            .subscribe((definition) => {
                this.setDefinition(definition);
            });
    }

    private setDefinition(definition: ProjectAnalysisDefinition): void {
        this.definition = definition;
        this.subscriptions.push(this.definition.onChange.subscribe(() => {
            this.updateCategoryTables();
        }));
        this.updateCategoryTables();
    }
}
